"use client";

import Link from "next/link";
import Image from "next/image";
import { handleButtonTracking } from "@/app/utils/button-tracking";

type Variant = { title: string; description: string; cta: string };

// A pool of varied product CTAs so every article doesn't read the same.
// Selection is deterministic (seeded by slug) so SSR and client render match.
const VARIANTS: Variant[] = [
  {
    title: "Skip the boilerplate, not the launch",
    description:
      "ExtensionFast ships with Stripe payments, auth, and a Chrome Web Store-ready manifest, so you build features instead of plumbing.",
    cta: "Get ExtensionFast",
  },
  {
    title: "Go from idea to the Chrome Web Store in days",
    description:
      "Start from a production-ready boilerplate with payments, auth, and onboarding already wired up and waiting for your idea.",
    cta: "See what's included",
  },
  {
    title: "Stop rebuilding the same extension setup",
    description:
      "Auth, payments, an options page, and Manifest V3 config come pre-built, so your weekend project actually ships.",
    cta: "Start building faster",
  },
  {
    title: "The plumbing is done. Bring the idea.",
    description:
      "ExtensionFast gives you Supabase auth, Stripe billing, and a setup that passes Chrome Web Store review on the first try.",
    cta: "Explore ExtensionFast",
  },
  {
    title: "Your next extension could be live this week",
    description:
      "Let ExtensionFast handle the infrastructure so you can focus on the one feature your users actually came for.",
    cta: "Launch with ExtensionFast",
  },
  {
    title: "Built for developers who'd rather ship",
    description:
      "Payments, auth, and store-ready scaffolding in one boilerplate, so launch day arrives weeks sooner.",
    cta: "Get ExtensionFast",
  },
];

// Small, stable string hash so the same slug always picks the same variant.
function hashSeed(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h << 5) - h + seed.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

export interface BlogCTAProps {
  title?: string;
  description?: string;
  cta?: string;
  seed?: string;
  // When these are present, render an image-rich "related post" card instead
  // of the product CTA.
  imageLink?: string;
  imageAlt?: string;
  link?: string;
}

function BlogCTA({
  title,
  description,
  cta,
  seed,
  imageLink,
  imageAlt,
  link,
}: BlogCTAProps) {
  // ----- Related-post card mode -----
  if (link) {
    const href = link.replace(/^https?:\/\/(www\.)?extensionfast\.com/i, "");
    return (
      <div className="w-full my-4">
        <Link
          href={href || link}
          className="group flex flex-col overflow-hidden rounded-2xl border border-line bg-panel transition-all hover:-translate-y-0.5 hover:border-zinc-700 hover:shadow-md sm:flex-row"
        >
          {imageLink && (
            <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-ink-soft sm:aspect-auto sm:w-48">
              <Image
                src={imageLink}
                alt={imageAlt ?? title ?? "Related article"}
                fill
                sizes="(max-width: 640px) 100vw, 192px"
                className="object-cover transition-transform duration-300 group-hover:scale-105"
              />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5 p-5">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-primary">
              Recommended reading
            </span>
            {title && (
              <p className="text-base font-bold leading-snug text-white transition-colors group-hover:text-primary">
                {title}
              </p>
            )}
            {description && (
              <p className="text-sm leading-relaxed text-zinc-400">
                {description}
              </p>
            )}
            <span className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-primary transition-all group-hover:gap-2">
              {cta ?? "Read more"}
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </span>
          </div>
        </Link>
      </div>
    );
  }

  // ----- Product CTA mode (varied per article) -----
  const variant = VARIANTS[seed ? hashSeed(seed) % VARIANTS.length : 0];
  const heading = title ?? variant.title;
  const body = description ?? variant.description;
  const buttonLabel = cta ?? variant.cta;

  return (
    <div className="w-full my-4">
      <div className="relative overflow-hidden rounded-2xl border border-primary/15 bg-gradient-to-br from-primary/[0.12] via-panel to-ink-soft p-6 sm:p-7">
        {/* decorative glow */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
        />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
              <svg
                className="h-3 w-3"
                fill="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M12 2 L13.6 9.4 L21 11 L13.6 12.6 L12 20 L10.4 12.6 L3 11 L10.4 9.4 Z" />
              </svg>
              ExtensionFast
            </span>
            <p className="mt-3 text-lg font-bold leading-snug text-white">
              {heading}
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-zinc-400">
              {body}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              handleButtonTracking("clicked_blog_cta");
              window.location.href = "/#pricing";
            }}
            className="group inline-flex shrink-0 items-center justify-center gap-1.5 self-start rounded-full ring-1 ring-inset ring-white/15 bg-primary px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-secondary hover:shadow-md sm:self-auto"
          >
            {buttonLabel}
            <svg
              className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BlogCTA;
